/**
 * Bind live input masks onto Woo Blocks core address inputs for default
 * text/tel fields that have mask settings. Custom DynamicField / MaskedInputField
 * paths are intentionally untouched.
 */
import { useEffect, useRef } from 'react';
import { getDefaultFieldsName } from './helper-fronted';
import {
	getInputMaskConfig,
	getInputMaskFormatHint,
	getInputMaskValidationErrorMessage,
	hasInputMask,
	isInputMaskValueValid,
} from './inputmask-config';
import { isInlineValidationEnabled } from './input-validations';

const BOUND_FLAG = 'data-fme-ccfw-default-mask-bound';
const FIELD_NAME_ATTR = 'data-fme-ccfw-default-mask-field';
const TEXT_TEL_TYPES = new Set([ 'text', 'tel' ]);

let placeOrderHookRegistered = false;

/**
 * @param {string} fieldName e.g. shipping_first_name
 * @returns {'billing'|'shipping'|null}
 */
function getAddressType( fieldName ) {
	if ( String( fieldName || '' ).startsWith( 'billing_' ) ) {
		return 'billing';
	}
	if ( String( fieldName || '' ).startsWith( 'shipping_' ) ) {
		return 'shipping';
	}
	return null;
}

/**
 * @param {string} fieldName e.g. shipping_first_name
 * @returns {string} Woo address store key e.g. first_name
 */
function getAddressStoreKey( fieldName ) {
	return String( fieldName || '' ).replace( /^(billing_|shipping_)/, '' );
}

/**
 * Normalize localized field lists (array or PHP-style object).
 *
 * @param {Array|Object|*} allFields
 * @returns {Array}
 */
function normalizeFieldsList( allFields ) {
	if ( Array.isArray( allFields ) ) {
		return allFields;
	}
	if ( allFields && typeof allFields === 'object' ) {
		return Object.values( allFields );
	}
	return [];
}

/**
 * Default text/tel fields for a section that have a configured mask.
 *
 * @param {Array|Object} allFields
 * @param {'billing'|'shipping'} section
 * @returns {Array}
 */
export function getMaskedDefaultFields( allFields, section ) {
	const prefix = `${ section }_`;
	const nameMap = getDefaultFieldsName();

	return normalizeFieldsList( allFields ).filter( ( field ) => {
		if ( ! field || field.field_mode !== 'default' ) {
			return false;
		}
		if ( String( field.is_enable ) === '0' ) {
			return false;
		}
		if ( ! TEXT_TEL_TYPES.has( field.field_type ) ) {
			return false;
		}
		const name = field.field_name;
		if ( ! name || ! String( name ).startsWith( prefix ) ) {
			return false;
		}
		if ( ! nameMap[ name ] ) {
			return false;
		}
		return hasInputMask( field );
	} );
}

/**
 * @param {HTMLElement} el
 * @param {object} config
 * @param {string} formatHint
 */
function applyMaskDataAttributes( el, config, formatHint ) {
	const payload = {
		mask: config.mask,
		maskType: config.maskType,
		formatHint: formatHint || config.formatHint || '',
	};

	el.setAttribute( 'data-fme-input-mask', config.mask );
	el.setAttribute( 'data-fme-input-mask-type', config.maskType || 'static' );
	el.setAttribute( 'data-fme-input-mask-config', JSON.stringify( payload ) );
	if ( payload.formatHint ) {
		el.setAttribute( 'data-fme-input-mask-hint', payload.formatHint );
	}
}

/**
 * Sync remasked DOM value into the Woo cart address store.
 * No-ops when the store already holds the same value (avoids React remount loops).
 *
 * @param {string} fieldName
 * @param {string} value
 */
function syncValueToAddressStore( fieldName, value ) {
	const addressType = getAddressType( fieldName );
	const storeKey = getAddressStoreKey( fieldName );
	if ( ! addressType || ! storeKey || ! window.wp?.data?.dispatch || ! window.wp?.data?.select ) {
		return;
	}

	const nextValue = value == null ? '' : String( value );

	try {
		const cart = window.wp.data.select( 'wc/store/cart' );
		const currentAddress =
			addressType === 'billing'
				? ( cart?.getCartData?.()?.billingAddress || cart?.getCustomerData?.()?.billingAddress || {} )
				: ( cart?.getCartData?.()?.shippingAddress || cart?.getCustomerData?.()?.shippingAddress || {} );

		if ( String( currentAddress?.[ storeKey ] ?? '' ) === nextValue ) {
			return;
		}

		const cartDispatch = window.wp.data.dispatch( 'wc/store/cart' );
		if ( ! cartDispatch ) {
			return;
		}

		const patch = { [ storeKey ]: nextValue };

		if ( addressType === 'billing' && typeof cartDispatch.setBillingAddress === 'function' ) {
			cartDispatch.setBillingAddress( patch );
		} else if ( addressType === 'shipping' && typeof cartDispatch.setShippingAddress === 'function' ) {
			cartDispatch.setShippingAddress( patch );
		}
	} catch ( e ) {
		// Ignore store sync failures (store may be mid-update).
	}
}

/**
 * Woo Blocks address fields use errorId `${type}_${key}` (e.g. shipping_first_name).
 * That matches our field_name — do not invent alternate keys.
 *
 * @param {string} fieldName
 * @param {string} message Full sentence for ValidationInputError (not a fragment)
 * @param {boolean} isValid
 */
function setWooValidationState( fieldName, message, isValid ) {
	if ( ! window.wp?.data?.dispatch ) {
		return;
	}

	try {
		const validation = window.wp.data.dispatch( 'wc/store/validation' );
		if ( ! validation ) {
			return;
		}

		if ( isValid ) {
			if ( typeof validation.clearValidationError === 'function' ) {
				validation.clearValidationError( fieldName );
			}
			return;
		}

		const cleanMessage = String( message || '' )
			.replace( /\s*\|\s*$/, '' )
			.replace( /\s+/g, ' ' )
			.trim();

		if ( typeof validation.setValidationErrors === 'function' ) {
			validation.setValidationErrors( {
				[ fieldName ]: {
					message: cleanMessage || 'This field does not match the required format.',
					hidden: false,
				},
			} );
		}
	} catch ( e ) {
		// Ignore validation store failures.
	}
}

/**
 * Remove any legacy DOM error spans left beside Woo address inputs
 * (from earlier inject-after-input behavior). Never re-inject them.
 *
 * @param {HTMLElement} el
 */
function clearLegacyDomMaskError( el ) {
	if ( ! el || ! el.parentNode ) {
		return;
	}
	try {
		if ( typeof window.jQuery === 'function' ) {
			const $el = window.jQuery( el );
			$el.siblings( 'span.fme-ccfw-input-mask-error' ).remove();
			$el.next( 'span.fme-ccfw-input-mask-error' ).remove();
			return;
		}
		let sibling = el.nextElementSibling;
		while ( sibling && sibling.classList?.contains( 'fme-ccfw-input-mask-error' ) ) {
			const remove = sibling;
			sibling = sibling.nextElementSibling;
			remove.parentNode?.removeChild( remove );
		}
	} catch ( e ) {
		// Ignore DOM cleanup failures.
	}
}

/**
 * Blur / place-order validation for a bound default input.
 * Uses Woo Blocks validation store only — never injects free-floating text
 * beside the input (that breaks the address flex grid).
 *
 * @param {object} field
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function validateBoundDefaultInput( field, el ) {
	if ( ! field || ! el ) {
		return true;
	}

	clearLegacyDomMaskError( el );

	const value = el.value;
	const valid = isInputMaskValueValid( value, field );
	const message = getInputMaskValidationErrorMessage( field );

	// Do NOT call fmeCcfwValidateMaskedInput here — its classic-checkout path
	// inserts a <span> after the input and breaks Blocks address layout.
	setWooValidationState( field.field_name, message, valid );
	return valid;
}

/**
 * @param {object} field
 * @returns {boolean}
 */
function bindOneDefaultMaskedField( field ) {
	const nameMap = getDefaultFieldsName();
	const domId = nameMap[ field.field_name ];
	if ( ! domId ) {
		return false;
	}

	const el = document.getElementById( domId );
	if ( ! el || el.tagName !== 'INPUT' ) {
		return false;
	}

	const config = getInputMaskConfig( field );
	if ( ! config ) {
		return false;
	}

	const formatHint = getInputMaskFormatHint( field ) || field.input_mask_format_hint || '';
	applyMaskDataAttributes( el, config, formatHint );
	el.setAttribute( FIELD_NAME_ATTR, field.field_name );

	const alreadyBound =
		el.getAttribute( BOUND_FLAG ) === '1' ||
		( typeof window.jQuery === 'function' && window.jQuery( el ).data( 'fmeCcfwInputMaskBound' ) );

	if ( ! alreadyBound && typeof window.jQuery === 'function' && typeof window.fmeCcfwInitInputmask === 'function' ) {
		window.fmeCcfwInitInputmask( window.jQuery( el ), {
			mask: config.mask,
			maskType: config.maskType,
			formatHint,
		} );
	}

	if ( el.getAttribute( BOUND_FLAG ) === '1' ) {
		return true;
	}

	/**
	 * Push remasked DOM → Woo address store immediately.
	 * A delayed sync leaves a window where React re-renders from the stale
	 * controlled store value and restores the previous input (sticky clear/retype).
	 * Inputmask Backspace/Delete uses preventDefault + jQuery.val() and may not
	 * emit a native `input` event — keydown sync covers that path.
	 */
	const flushStoreSync = () => {
		syncValueToAddressStore( field.field_name, el.value );
	};

	const onInput = () => {
		flushStoreSync();
		clearLegacyDomMaskError( el );
		setWooValidationState( field.field_name, '', true );
	};

	const onKeyDown = ( e ) => {
		if ( e.key !== 'Backspace' && e.key !== 'Delete' ) {
			return;
		}
		// Inputmask's keydown is registered first and has already remasked via
		// jQuery.val() (often without a native `input` event). Flush now so the
		// Woo controlled store matches DOM before any re-render restores stale value.
		flushStoreSync();
		clearLegacyDomMaskError( el );
		setWooValidationState( field.field_name, '', true );
	};

	const onBlur = () => {
		flushStoreSync();
		if ( isInlineValidationEnabled() ) {
			validateBoundDefaultInput( field, el );
		}
	};

	el.addEventListener( 'input', onInput );
	el.addEventListener( 'change', flushStoreSync );
	el.addEventListener( 'keydown', onKeyDown );
	el.addEventListener( 'blur', onBlur );
	el.setAttribute( BOUND_FLAG, '1' );

	// Drop any leftover sibling error spans from prior inject-after-input behavior.
	clearLegacyDomMaskError( el );

	// Initial sync when remask reformats an existing value (diff-guarded).
	flushStoreSync();

	return true;
}

/**
 * Bind masks for default fields in a section. Safe to call repeatedly
 * (skips already-bound inputs; picks up newly rendered ones).
 *
 * @param {object} options
 * @param {'billing'|'shipping'} options.section
 * @param {Array|Object} options.allFields
 * @returns {number} Newly or already bound count
 */
export function bindDefaultFieldMasks( { section, allFields } ) {
	const fields = getMaskedDefaultFields( allFields, section );
	let bound = 0;

	fields.forEach( ( field ) => {
		if ( bindOneDefaultMaskedField( field ) ) {
			bound += 1;
		}
	} );

	if ( typeof window.fmeCcfwScheduleInputmaskInit === 'function' ) {
		window.fmeCcfwScheduleInputmaskInit( document );
	}

	return bound;
}

/**
 * Validate all bound default masked inputs (blur / place-order).
 *
 * @param {Array|Object} allFields
 * @param {'billing'|'shipping'|null} section Optional section filter
 * @returns {boolean}
 */
export function validateDefaultFieldMasks( allFields, section = null ) {
	const sections = section ? [ section ] : [ 'billing', 'shipping' ];
	let allValid = true;
	let firstInvalid = null;

	sections.forEach( ( sec ) => {
		getMaskedDefaultFields( allFields, sec ).forEach( ( field ) => {
			const domId = getDefaultFieldsName()[ field.field_name ];
			if ( ! domId ) {
				return;
			}
			const el = document.getElementById( domId );
			if ( ! el || el.getAttribute( BOUND_FLAG ) !== '1' ) {
				return;
			}
			if ( ! validateBoundDefaultInput( field, el ) ) {
				allValid = false;
				if ( ! firstInvalid ) {
					firstInvalid = el;
				}
			}
		} );
	} );

	if ( firstInvalid ) {
		try {
			firstInvalid.focus();
			firstInvalid.scrollIntoView( { behavior: 'smooth', block: 'center' } );
		} catch ( e ) {
			// Ignore focus/scroll failures.
		}
	}

	return allValid;
}

/**
 * Register place-order client validation once (Woo Blocks checkout events).
 *
 * @param {Function} getAllFieldsFn Returns combined field lists for validation
 */
export function registerDefaultMaskPlaceOrderValidation( getAllFieldsFn ) {
	if ( placeOrderHookRegistered ) {
		return;
	}
	placeOrderHookRegistered = true;

	const checkoutEvents = window.wc?.blocksCheckoutEvents?.checkoutEvents;
	if ( ! checkoutEvents || typeof checkoutEvents.onCheckoutValidation !== 'function' ) {
		return;
	}

	checkoutEvents.onCheckoutValidation( () => {
		const allFields = typeof getAllFieldsFn === 'function' ? getAllFieldsFn() : [];
		const valid = validateDefaultFieldMasks( allFields );

		if ( valid ) {
			return true;
		}

		const responseTypes = window.wc?.wcTypes?.responseTypes;
		const message =
			( typeof fme_upload_data !== 'undefined' && fme_upload_data?.i18n?.input_mask_format_simple )
				? 'Please correct the highlighted fields.'
				: 'Please correct the highlighted fields.';

		if ( responseTypes?.ERROR ) {
			return { type: responseTypes.ERROR, message };
		}

		return { type: 'error', message };
	} );
}

/**
 * React hook: bind + re-bind default masks when address inputs mount;
 * register place-order validation once.
 *
 * Do not pass address-store values as effect deps — our DOM→store sync updates
 * those values, and re-running bind on every keystroke fights controlled inputs.
 * MutationObserver + mount retries pick up newly rendered Woo address fields.
 *
 * @param {'billing'|'shipping'} section
 * @param {Array|Object} allFields
 * @param {Array} [extraDeps] Optional extra deps (avoid address objects/triggers)
 */
export function useDefaultFieldMasks( section, allFields, extraDeps = [] ) {
	const observerRef = useRef( null );
	const retryTimersRef = useRef( [] );
	const bindDebounceRef = useRef( null );

	useEffect( () => {
		const runBind = () => {
			bindDefaultFieldMasks( { section, allFields } );
		};

		runBind();

		// Retry while Woo address inputs mount asynchronously.
		retryTimersRef.current.forEach( ( id ) => clearTimeout( id ) );
		retryTimersRef.current = [ 0, 200, 500, 1000, 2000 ].map( ( delay ) =>
			setTimeout( runBind, delay )
		);

		registerDefaultMaskPlaceOrderValidation( () => {
			const billing =
				typeof fme_billing_all_fields_data !== 'undefined' ? fme_billing_all_fields_data : [];
			const shipping =
				typeof fme_shipping_all_fields_data !== 'undefined' ? fme_shipping_all_fields_data : [];
			return [ ...normalizeFieldsList( billing ), ...normalizeFieldsList( shipping ) ];
		} );

		const root =
			document.querySelector( '.wp-block-woocommerce-checkout' ) ||
			document.querySelector( '.wc-block-checkout' ) ||
			document.body;

		if ( typeof MutationObserver === 'function' && root ) {
			if ( observerRef.current ) {
				observerRef.current.disconnect();
			}
			observerRef.current = new MutationObserver( () => {
				if ( bindDebounceRef.current ) {
					clearTimeout( bindDebounceRef.current );
				}
				// childList-only: ignore value/attr churn from remask + store sync.
				bindDebounceRef.current = setTimeout( runBind, 100 );
			} );
			observerRef.current.observe( root, { childList: true, subtree: true } );
		}

		return () => {
			retryTimersRef.current.forEach( ( id ) => clearTimeout( id ) );
			retryTimersRef.current = [];
			if ( bindDebounceRef.current ) {
				clearTimeout( bindDebounceRef.current );
				bindDebounceRef.current = null;
			}
			if ( observerRef.current ) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- extraDeps passed by callers
	}, [ section, allFields, ...extraDeps ] );
}
